import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useLocation, Link, useNavigate } from "react-router-dom";
import hotToast from "react-hot-toast";
import Cookies from "js-cookie";
import { clearSession } from "../utils/session";

import api from "../pages/chat/api";
import { getOrFetchSubscriptionStatus, hasPersonalPaidPlan } from "../utils/subscriptionStatus";
import { fetchRfpSubscription } from "../services/rfpBillingApi";
import { switchWorkspace } from "../utils/workspace";
import { Avatar, getInitials, WorkspaceMenuItems } from "../components/WorkspaceMenu";
import { useOnboarding } from "../onboarding/onboardingStore";
import {
  extractorStudioIcon,
  extractorStudioFilledIcon,
  projectsIcon,
  projectsFilledIcon,
  DashboardIcon,
  FilledDashboardIcon,
  analyticsIcon,
  analyticsFilledIcon,
  KbIcon,
  KbFilledIcon,
  CopilotIcon,
  FilledCopilotIcon,
} from "./svgIcons";

/* -------------------------------------------------------------------------- */
/*  Navigation configuration                                                  */
/* -------------------------------------------------------------------------- */

// Grouped items render under a section label with a vertical rail.
// A government landmark (columns + pediment): these are federal tenders, and
// the glyph matches the filled Material style of the neighbouring icons.
const tenderSearchIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M4 10v7h3v-7H4zm6 0v7h3v-7h-3zM2 22h19v-3H2v3zm14-12v7h3v-7h-3zm-4.5-9L2 6v2h19V6l-9.5-5z" fill="#C4C4C4" />
  </svg>
);

const tenderSearchFilledIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M4 10v7h3v-7H4zm6 0v7h3v-7h-3zM2 22h19v-3H2v3zm14-12v7h3v-7h-3zm-4.5-9L2 6v2h19V6l-9.5-5z" fill="#FAFAFA" />
  </svg>
);

const NAV_SECTIONS = [
  {
    label: "RFP Manager",
    items: [
      {
        title: "Dashboard",
        link: "/rfp-dashboard",
        icon: DashboardIcon,
        filledIcon: FilledDashboardIcon,
      },
      {
        title: "Sensa",
        link: "/rfp-copilot",
        icon: CopilotIcon,
        filledIcon: FilledCopilotIcon,
      },
      {
        title: "Knowledge Base",
        link: "/rfp-knowledgeBase/All-sources",
        icon: KbIcon,
        filledIcon: KbFilledIcon,
      },
      {
        title: "Projects",
        link: "/rfp-projects",
        icon: projectsIcon,
        filledIcon: projectsFilledIcon,
      },
      {
        title: "Tender Search",
        // Lands on Search; Discover is one click away in the page's own switch.
        // `link` is the section prefix, so the row is active on both.
        link: "/tender-search",
        icon: tenderSearchIcon,
        filledIcon: tenderSearchFilledIcon,
      },
      {
        title: "Analytics",
        link: "/analytics",
        icon: analyticsIcon,
        filledIcon: analyticsFilledIcon,
      },
    ],
  },
];

// Stand-alone items render flush (no section label / rail).
const STANDALONE_ITEMS = [];

const libraryIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M12 2 2 7l10 5 10-5-10-5Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M2 17l10 5 10-5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M2 12l10 5 10-5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// The Extractor Studio section renders with a checkmark on its active item
// (see ExtractorNavRow) instead of the solid-fill treatment NavRow uses, so
// it's kept out of NAV_SECTIONS rather than made a generic section variant.
// Both items share one route ("/extractor") and are distinguished by the
// ?view= query string, since the Studio page manages its own view state.
const EXTRACTOR_SECTION_LABEL = "Extractor Studio";
const EXTRACTOR_ITEMS = [
  {
    title: "Template Builder",
    link: "/extractor",
    icon: extractorStudioIcon,
    filledIcon: extractorStudioFilledIcon,
    extractorView: "builder",
  },
  {
    title: "Library",
    link: "/extractor?view=library",
    icon: libraryIcon,
    filledIcon: libraryIcon,
    extractorView: "library",
  },
];

// Flattened list used by the collapsed (icon-only) rail.
const FLAT_NAV_ITEMS = [
  ...NAV_SECTIONS.flatMap((s) => s.items),
  ...STANDALONE_ITEMS,
  ...EXTRACTOR_ITEMS,
];

/* -------------------------------------------------------------------------- */
/*  Inline icons (currentColor so they adapt to dark / light surfaces)        */
/* -------------------------------------------------------------------------- */

const PanelIcon = (props) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" {...props}>
    <rect
      x="3"
      y="4"
      width="18"
      height="16"
      rx="2"
      stroke="currentColor"
      strokeWidth="1.7"
    />
    <path d="M9 4v16" stroke="currentColor" strokeWidth="1.7" />
  </svg>
);

const ChevronsUpDownIcon = (props) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" {...props}>
    <path
      d="M8 9l4-4 4 4M8 15l4 4 4-4"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronRightIcon = (props) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" {...props}>
    <path
      d="M9 6l6 6-6 6"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CheckIcon = (props) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" {...props}>
    <path
      d="M5 13l4 4L19 7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const GearIcon = (props) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c.2.61.78 1.05 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
      stroke="currentColor"
      strokeWidth="1.4"
    />
  </svg>
);

const LogoutIcon = (props) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" {...props}>
    <path
      d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16 17l5-5-5-5M21 12H9"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* -------------------------------------------------------------------------- */
/*  Sidebar                                                                   */
/* -------------------------------------------------------------------------- */

const Sidebar = ({ isOpen, toggleMenu, setDisplayName }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname.toLowerCase();

  // The Extractor Studio section shares one route across both its items,
  // distinguished by ?view=, so it needs its own active check.
  const isExtractorViewActive = (extractorView) => {
    if (pathname !== "/extractor") return false;
    const qView = new URLSearchParams(location.search).get("view") === "library" ? "library" : "builder";
    return qView === extractorView;
  };

  /* ---- workspace state ---- */
  const [workspaces, setWorkspaces] = useState(() => {
    const saved = localStorage.getItem("cachedWorkspaces");
    return saved ? JSON.parse(saved) : [];
  });
  const [activeWorkspace, setActiveWorkspace] = useState(() => {
    const savedActive = localStorage.getItem("activeWorkspace");
    if (savedActive) return JSON.parse(savedActive);
    const savedList = localStorage.getItem("cachedWorkspaces");
    if (savedList) {
      const parsed = JSON.parse(savedList);
      if (parsed.length > 0) return parsed[0];
    }
    return null;
  });
  const [isWorkspaceDropdownOpen, setIsWorkspaceDropdownOpen] = useState(false);
  const [isFetchingWorkspaces, setIsFetchingWorkspaces] = useState(false);

  useEffect(() => {
    const onWorkspaceChange = (e) => e.detail && setActiveWorkspace(e.detail);
    window.addEventListener("workspace-changed", onWorkspaceChange);
    return () => window.removeEventListener("workspace-changed", onWorkspaceChange);
  }, []);

  useEffect(() => {
    const onWorkspaceRenamed = (e) => {
      const { id, name } = e.detail || {};
      if (!id) return;
      const rename = (w) => (String(w?.id) === String(id) ? { ...w, name } : w);
      setWorkspaces((list) => list.map(rename));
      setActiveWorkspace((w) => (w ? rename(w) : w));
    };
    window.addEventListener("workspace-renamed", onWorkspaceRenamed);
    return () => window.removeEventListener("workspace-renamed", onWorkspaceRenamed);
  }, []);

  // The current workspace's RFP Manager plan. The trial card near the bottom shows while it's on
  // its free trial; a paid plan hides it, and an ended trial is handled by the blocking popup.
  const [rfpPlan, setRfpPlan] = useState(null);
  useEffect(() => {
    const loadRfpAccess = () => {
      fetchRfpSubscription()
        .then(setRfpPlan)
        .catch(() => setRfpPlan(null));
    };
    loadRfpAccess();
    window.addEventListener("workspace-changed", loadRfpAccess);
    return () => window.removeEventListener("workspace-changed", loadRfpAccess);
  }, []);

  /* ---- user / menu state ---- */
  const [user, setUser] = useState({ full_name: "", email: "", username: "" });
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  /* ---- subscription state ---- */
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [isShowPaymentPopup, setIsShowPaymentPopup] = useState(false);
  const [showGetStarted, setShowGetStarted] = useState(false);

  const workspaceRef = useRef(null);
  const userMenuRef = useRef(null);

  /* ---- portal menus: trigger + menu refs and screen positions ---- */
  // The dropdowns render into document.body so the page's stacking
  // contexts (sticky table headers, etc.) can't paint over them.
  const workspaceBtnRef = useRef(null);
  const userBtnRef = useRef(null);
  const wsMenuElRef = useRef(null);
  const userMenuElRef = useRef(null);
  const [wsMenuPos, setWsMenuPos] = useState(null);
  const [userMenuPos, setUserMenuPos] = useState(null);

  // Tooltip shown next to collapsed-sidebar icons
  const [iconTip, setIconTip] = useState(null);

  useEffect(() => {
    if (!isWorkspaceDropdownOpen) return;
    const place = () => {
      const r = workspaceBtnRef.current?.getBoundingClientRect();
      if (r) setWsMenuPos({ top: r.top, left: r.right + 12 });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [isWorkspaceDropdownOpen]);

  useEffect(() => {
    if (!isUserMenuOpen) return;
    const place = () => {
      const r = userBtnRef.current?.getBoundingClientRect();
      if (r)
        setUserMenuPos({
          bottom: window.innerHeight - r.bottom,
          left: r.right + 12,
        });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [isUserMenuOpen]);

  /* ---- only animate content on an actual collapse/expand toggle ---- */
  /* (prevents the entrance animation from replaying on route changes)  */
  const [isToggling, setIsToggling] = useState(false);
  const prevOpenRef = useRef(isOpen);
  useEffect(() => {
    if (prevOpenRef.current !== isOpen) {
      prevOpenRef.current = isOpen;
      setIsToggling(true);
      const t = setTimeout(() => setIsToggling(false), 320);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  /* ---- close popovers on outside click ---- */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        workspaceRef.current &&
        !workspaceRef.current.contains(event.target) &&
        !wsMenuElRef.current?.contains(event.target)
      ) {
        setIsWorkspaceDropdownOpen(false);
      }
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target) &&
        !userMenuElRef.current?.contains(event.target)
      ) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ---- load user from localStorage ---- */
  useEffect(() => {
    const full_name =
      localStorage.getItem("full_name") || localStorage.getItem("fullName");
    const email = localStorage.getItem("email");
    const username = localStorage.getItem("username");
    setUser({ full_name, email, username });
    if (typeof setDisplayName === "function") {
      setDisplayName(full_name || username);
    }
  }, [setDisplayName]);

  /* ---- subscription status + trial countdown ---- */
  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      try {
        const response = await getOrFetchSubscriptionStatus(api);
        if (response.status === 200) {
          const data = response.data;
          setSubscriptionStatus(data);
          if (data.expires_on) Cookies.set("expires_on", data.expires_on);
        }
      } catch (error) {
        console.error("Error fetching subscription status:", error);
      }
    };

    fetchSubscriptionStatus();
  }, []);

  /* ---- workspace handlers (unchanged behaviour) ---- */
  const toggleWorkspaceDropdown = async () => {
    const willBeOpen = !isWorkspaceDropdownOpen;
    setIsWorkspaceDropdownOpen(willBeOpen);
    if (!willBeOpen) return;

    setIsFetchingWorkspaces(true);
    try {
      const response = await api.get("/api/v1/org/");
      if (response.status === 200 && Array.isArray(response.data)) {
        setWorkspaces(response.data);
        localStorage.setItem("cachedWorkspaces", JSON.stringify(response.data));
        const fresh = activeWorkspace && response.data.find((w) => String(w.id) === String(activeWorkspace.id));
        if (fresh && fresh.name !== activeWorkspace.name) {
          setActiveWorkspace(fresh);
          localStorage.setItem("activeWorkspace", JSON.stringify(fresh));
        }
        if (!activeWorkspace && response.data.length > 0) {
          setActiveWorkspace(response.data[0]);
          localStorage.setItem(
            "activeWorkspace",
            JSON.stringify(response.data[0]),
          );
        }
      }
    } catch (error) {
      console.error("Error fetching workspaces:", error);
    } finally {
      setIsFetchingWorkspaces(false);
    }
  };

  const handleWorkspaceChange = (workspace) => {
    setActiveWorkspace(workspace);
    setIsWorkspaceDropdownOpen(false);
    hotToast.success(`Switched to ${workspace.name}`);
    switchWorkspace(workspace);
  };

  const inOnboarding = useOnboarding((s) => !!s.state);
  const setToursMenuOpen = useOnboarding((s) => s.setToursMenuOpen);

  const goToOrgSettings = () => {
    setIsWorkspaceDropdownOpen(false);
    navigate("/account");
  };

  /* ---- logout ---- */
  // The clearing itself lives in utils/session so Sensa's rail can log out with
  // exactly the same list. Only the redirect is decided here.
  const handleLogout = () => {
    clearSession();
    navigate("/signin", { replace: true });
  };

  /* ---- active-route detection (unchanged behaviour) ---- */
  const isActive = (link) => {
    if (!link) return false;
    const normalizedLink = link.toLowerCase();
    if (pathname === normalizedLink || pathname.startsWith(normalizedLink))
      return true;
    if (
      normalizedLink === "/response_writer" &&
      pathname.startsWith("/create-proposal-question")
    ) {
      return true;
    }
    return false;
  };

  /* ---- routes where the sidebar should not render (unchanged) ---- */
  const isChatPage =
    pathname.includes("/chat") || pathname === "/rfp-knowledgebase/chat";
  const isContractChat = pathname.includes("contractchat");
  const isInvoiceChat = pathname.includes("/invoices/invoice_chat");
  const isExcelChat = pathname.includes("/spreadsheets/excelchat");
  const isCreatedProposal =
    pathname.includes("/created-proposal-finalpage") ||
    pathname.includes("/created_proposal");

  if (isChatPage) return null;
  if (isCreatedProposal) return null;
  if (pathname === "/payment") return null;
  if (pathname === "/rfp/rfp_chat") return null;
  if (isContractChat) return null;
  if (isExcelChat) return null;
  if (isInvoiceChat) return null;
  if (pathname === "/contract/folderchat") return null;

  const displayName = `${user.full_name || user.username || ""}`.trim();

  // Entrance animations apply only during a collapse/expand toggle.
  const contentAnim = isToggling ? "animate-sidebar-content-in" : "";
  const fadeAnim = isToggling ? "animate-sidebar-fade-in" : "";

  const trialDaysLeft = rfpPlan?.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(rfpPlan.trial_ends_at).getTime() - Date.now()) / 86400000))
    : null;
  const showPromoCard =
    isOpen &&
    !isUserMenuOpen &&
    rfpPlan?.state === "trialing" &&
    trialDaysLeft !== null &&
    !hasPersonalPaidPlan(subscriptionStatus);

  /* ---- a single nav row (expanded) ---- */
  const NavRow = ({ item }) => {
    const active = isActive(item.link);
    return (
      <Link
        to={item.to || item.link}
        data-tour={`nav-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
        className={`flex items-center gap-2 text-[15px] rounded-[4px] px-3 py-2 mb-1 transition-[background-color,box-shadow,color] duration-150 ${
          active
            ? "bg-[#A04A97] text-white font-medium"
            : "text-[#C4C4C4] hover:bg-[rgba(255,255,255,0.15)] hover:shadow-[0_1px_2px_0_rgba(255,255,255,0.10)]"
        }`}
      >
        <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
          {active ? item.filledIcon || item.icon : item.icon}
        </span>
        <span className="whitespace-nowrap">{item.title}</span>
      </Link>
    );
  };

  /* Extractor Studio's active row uses a translucent pill + checkmark instead
     of NavRow's solid fill, matching the design handoff. */
  const ExtractorNavRow = ({ item }) => {
    const active = isExtractorViewActive(item.extractorView);
    return (
      <Link
        to={item.link}
        className={`flex items-center gap-2 text-[15px] rounded-[4px] px-3 py-2 mb-1 transition-[background-color,box-shadow,color] duration-150 ${
          active
            ? "bg-[rgba(255,255,255,0.14)] text-white font-medium"
            : "text-[#C4C4C4] hover:bg-[rgba(255,255,255,0.15)] hover:shadow-[0_1px_2px_0_rgba(255,255,255,0.10)]"
        }`}
      >
        <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
          {active ? item.filledIcon || item.icon : item.icon}
        </span>
        <span className="whitespace-nowrap flex-1">{item.title}</span>
        {active && <CheckIcon className="text-white flex-shrink-0" width={16} height={16} />}
      </Link>
    );
  };

  return (
    <div
      className={`h-full md:h-screen bg-[#2C1338] font-outfit transition-[width] duration-300 ease-sidebar will-change-[width] flex-shrink-0 relative z-50 overflow-x-hidden ${
        isOpen ? "w-[240px]" : "w-[80px]"
      }`}
    >
      <nav className="w-full h-full flex flex-col">
        {/* ----------------------------- Header ----------------------------- */}
        {isOpen ? (
          <div className={`px-4 pt-4 ${contentAnim}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img
                  src="/docusensas-logo.svg"
                  alt="DocuSensa"
                  className="w-[22px] h-[22px] flex-shrink-0"
                />
                <span className="text-white text-[18px] font-semibold tracking-tight">
                  DocuSensa
                </span>
              </div>
              <button
                onClick={toggleMenu}
                aria-label="Collapse sidebar"
                className="text-[#C9A9D6] hover:text-white transition-colors"
              >
                <PanelIcon />
              </button>
            </div>
            <div className="border-b border-[rgba(255,255,255,0.08)] mt-4" />
          </div>
        ) : (
          <div className="flex justify-center pt-4">
            <button
              onClick={toggleMenu}
              aria-label="Expand sidebar"
              title="Expand sidebar"
              className="group relative h-8 w-8 flex items-center justify-center text-[#C9A9D6] hover:text-white transition-colors"
            >
              {/* Default: DocuSensa robot logo */}
              <img
                src="/docusensas-logo.svg"
                alt="DocuSensa"
                className="w-[22px] h-[22px] transition-opacity duration-150 group-hover:opacity-0"
              />
              {/* On hover: expand (panel) icon */}
              <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                <PanelIcon />
              </span>
            </button>
          </div>
        )}

        {/* ------------------------ Workspace switcher ---------------------- */}
        <div
          ref={workspaceRef}
          className={`relative mt-4 ${isOpen ? "px-3" : "px-0 flex justify-center"}`}
        >
          <button
            ref={workspaceBtnRef}
            data-tour="workspace"
            onClick={toggleWorkspaceDropdown}
            title={!isOpen ? activeWorkspace?.name : undefined}
            className={`flex items-center bg-[rgba(255,255,255,0.06)] border border-[rgba(255,255,255,0.08)] hover:bg-[#FFFFFF1A] rounded-lg transition-colors duration-150 ${
              isOpen ? "w-full gap-2 px-2 py-2" : "p-1"
            }`}
          >
            <Avatar
              name={activeWorkspace?.name}
              shape="square"
              size={isOpen ? 30 : 32}
              bg="#A04A97"
              text="#FAFAFA"
            />
            {isOpen && (
              <>
                <div className={`flex flex-col items-start overflow-hidden flex-1 ${contentAnim}`}>
                  <span className="uppercase text-[12px] font-medium leading-normal tracking-[-0.12px] text-[rgba(199,199,199,0.90)]">
                    Workspace
                  </span>
                  <span className="text-[15px] font-semibold leading-[16px] tracking-[0.3px] truncate w-full text-left text-[#FAFAFA] mt-[3px]">
                    {activeWorkspace?.name || "Select Workspace"}
                  </span>
                </div>
                <ChevronsUpDownIcon className={`text-white [stroke-width:2.5] flex-shrink-0 ${fadeAnim}`} />
              </>
            )}
          </button>

          {isWorkspaceDropdownOpen &&
            wsMenuPos &&
            createPortal(
              <div
                ref={wsMenuElRef}
                style={{
                  position: "fixed",
                  top: wsMenuPos.top,
                  left: wsMenuPos.left,
                }}
                className="z-[100000] w-[240px] font-outfit bg-white rounded-md border-[0.5px] border-[#B3B3B3] shadow-[0_2px_4px_0_rgba(0,0,0,0.10)] py-1.5 max-h-72 overflow-y-auto"
              >
              <WorkspaceMenuItems
                workspaces={workspaces}
                activeId={activeWorkspace?.id}
                onSelect={handleWorkspaceChange}
              />

              {!isFetchingWorkspaces && workspaces.length === 0 && (
                <div className="px-3 py-3 text-xs text-gray-400 text-center">
                  No workspaces found
                </div>
              )}

              <div className="my-1 border-t border-gray-100 mx-2" />
              <button
                onClick={goToOrgSettings}
                className="w-full flex items-center font-medium justify-between px-3 py-2 text-[14px] text-[#00000080] hover:bg-[#F5F5F5] rounded-md transition-colors"
              >
                <span>Organization Settings</span>
                <ChevronRightIcon className="text-[#00000080] font-medium" />
              </button>
              </div>,
              document.body
            )}
        </div>

        {/* Separator under the workspace switcher */}
        {isOpen && <div className="mx-3 mt-4 border-b border-[#FFFFFF33]" />}

        {/* ----------------------------- Nav -------------------------------- */}
        {isOpen ? (
          <div className={`mt-3 pl-4 pr-2 flex-1 overflow-y-auto ${contentAnim}`}>
            {NAV_SECTIONS.map((section) => (
              <div key={section.label} className="mb-3">
                <p className=" mb-2 text-[12px] font-medium uppercase tracking-wider text-[#C7C7C7E5]">
                  {section.label}
                </p>
                <div className="relative pl-3">
                  <span className="absolute left-0 top-1 bottom-1 w-[1.5px] bg-[#FFFFFF33]" />
                  {section.items.map((item) => (
                    <NavRow key={item.title} item={item} />
                  ))}
                </div>
              </div>
            ))}
            {STANDALONE_ITEMS.map((item) => (
              <NavRow key={item.title} item={item} />
            ))}
            <div className="mb-3" data-tour="nav-extractor">
              <p className=" mb-2 text-[12px] font-medium uppercase tracking-wider text-[#C7C7C7E5]">
                {EXTRACTOR_SECTION_LABEL}
              </p>
              <div className="relative pl-3">
                <span className="absolute left-0 top-1 bottom-1 w-[1.5px] bg-[#FFFFFF33]" />
                {EXTRACTOR_ITEMS.map((item) => (
                  <ExtractorNavRow key={item.title} item={item} />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className={`mt-5 flex-1 flex flex-col items-center gap-2 overflow-y-auto ${fadeAnim}`}>
            {FLAT_NAV_ITEMS.map((item) => {
              const active = item.extractorView ? isExtractorViewActive(item.extractorView) : isActive(item.link);
              return (
                <Link
                  key={item.title}
                  to={item.to || item.link}
                  aria-label={item.title}
                  onMouseEnter={(e) => {
                    const r = e.currentTarget.getBoundingClientRect();
                    setIconTip({
                      label: item.title,
                      top: r.top + r.height / 2,
                      left: r.right + 12,
                    });
                  }}
                  onMouseLeave={() => setIconTip(null)}
                  className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
                    active
                      ? "bg-[#A04A97]"
                      : "hover:bg-[rgba(255,255,255,0.06)]"
                  }`}
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    {active ? item.filledIcon || item.icon : item.icon}
                  </span>
                </Link>
              );
            })}
          </div>
        )}

        {/* -------------------------- Promo card ---------------------------- */}
        {showPromoCard && (
          <div
            className="mx-3 mb-3 p-4 text-[#2C1338] font-outfit text-sm flex flex-col justify-between"
            style={{
              borderRadius: "12px",
              background:
                "linear-gradient(111deg, #F5EEFF 14.52%, #FDFDFD 99.31%)",
              boxShadow:
                "0px 7px 4px 0px rgba(255, 255, 255, 0.05), 0px 3px 3px 0px rgba(255, 255, 255, 0.09), 0px 1px 2px 0px rgba(255, 255, 255, 0.10)",
            }}
          >
            <div className="flex items-center mb-1">
              <img
                src="/trial-icon-billing.svg"
                alt=""
                className="w-[16px] h-[16px] mr-2"
              />
              <div
                className="text-[#18101C] text-[13px] font-medium"
                style={{ letterSpacing: "0.13px" }}
              >
                {`Trial Active: ${trialDaysLeft} ${trialDaysLeft === 1 ? "Day" : "Days"} Left`}
              </div>
            </div>

            <div
              className="text-center text-[13px] font-medium leading-snug tracking-[0.13px]"
              style={{ color: "#73607B" }}
            >
              Create up to{" "}
              <span style={gradientText}>{rfpPlan?.usage?.projects?.limit ?? 2} projects</span> and get{" "}
              <span style={gradientText}>unlimited tender search</span>
            </div>

            <div className="mt-2 text-center">
              <div className="text-[12px] mb-1 text-[#2C1338]">
                Need more power?
              </div>
              <button
                onClick={() => navigate("/account/billing")}
                className="inline-flex justify-center items-center rounded-[6px] text-[13px] font-medium tracking-[0.13px]"
                style={{
                  padding: "8px 20px",
                  background:
                    "linear-gradient(93deg, #6F52ED 0.64%, #845EC2 99.52%)",
                  color: "#F5F4F3",
                }}
              >
                Upgrade Now
              </button>
            </div>
          </div>
        )}

        {/* ------------------------- User profile --------------------------- */}
        <div
          ref={userMenuRef}
          className={`relative mt-auto pb-2 ${isOpen ? "px-3" : "px-0"}`}
        >
          {/* The rule goes with the avatar. On its own above an empty space it
              read as a section divider for a section that was not there. */}
          <div className="border-t border-[#FFFFFF33] mb-1 mx-1" />
          <button
            ref={userBtnRef}
            onClick={() => setIsUserMenuOpen((o) => !o)}
            title={!isOpen ? displayName : undefined}
            className={`flex items-center rounded-lg transition-colors duration-150 hover:bg-[rgba(255,255,255,0.06)] ${
              isOpen ? "w-full gap-2 px-2 py-2" : "w-full justify-center py-1"
            }`}
          >
            <span className="w-9 h-9 flex items-center justify-center flex-shrink-0 rounded-full bg-[#A04A97] text-[#FAFAFA] text-[13px] font-semibold leading-none">
              {getInitials(displayName)}
            </span>
            {isOpen && (
              <>
                <div className={`flex-1 text-left overflow-hidden ${contentAnim}`}>
                  <p className="text-[14px] font-medium leading-[16px] tracking-[0.14px] text-[#FAFAFA] truncate">
                    {displayName}
                  </p>
                  <p className="text-[12px] font-medium leading-normal tracking-[-0.12px] text-[#C7C7C7] truncate">
                    {user.email}
                  </p>
                </div>
                <ChevronsUpDownIcon className={`text-white [stroke-width:2.5] flex-shrink-0 ${fadeAnim}`} />
              </>
            )}
          </button>

          {isUserMenuOpen &&
            userMenuPos &&
            createPortal(
              <div
                ref={userMenuElRef}
                style={{
                  position: "fixed",
                  bottom: userMenuPos.bottom,
                  left: userMenuPos.left,
                }}
                className="z-[100000] w-[200px] font-outfit bg-white rounded-md border-[0.5px] border-[#B3B3B3] shadow-[0_2px_4px_0_rgba(0,0,0,0.10)] py-1.5 px-2"
              >
              <button
                onClick={() => {
                  setIsUserMenuOpen(false);
                  navigate("/account");
                }}
                className="w-full flex items-center justify-between px-3 py-1.5 text-[14px] text-[#00000080] hover:bg-[#F5F5F5] rounded-md transition-colors"
              >
                <span className="flex items-center gap-2.5">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="13"
                    height="14"
                    viewBox="0 0 13 14"
                    fill="none"
                  >
                    <path
                      d="M5.69774 14C5.39049 14 5.12592 13.895 4.90402 13.685C4.68212 13.475 4.54841 13.2183 4.50289 12.915L4.34926 11.76C4.20133 11.7017 4.06193 11.6317 3.93107 11.55C3.8002 11.4683 3.67218 11.3808 3.54701 11.2875L2.48871 11.7425C2.20422 11.8708 1.91973 11.8825 1.63524 11.7775C1.35075 11.6725 1.12885 11.4858 0.969538 11.2175L0.167279 9.7825C0.00796569 9.51417 -0.0375525 9.22833 0.0307248 8.925C0.0990021 8.62167 0.252626 8.37083 0.491597 8.1725L1.39627 7.4725C1.38489 7.39083 1.3792 7.31208 1.3792 7.23625V6.76375C1.3792 6.68792 1.38489 6.60917 1.39627 6.5275L0.491597 5.8275C0.252626 5.62917 0.0990021 5.37833 0.0307248 5.075C-0.0375525 4.77167 0.00796569 4.48583 0.167279 4.2175L0.969538 2.7825C1.12885 2.51417 1.35075 2.3275 1.63524 2.2225C1.91973 2.1175 2.20422 2.12917 2.48871 2.2575L3.54701 2.7125C3.67218 2.61917 3.80305 2.53167 3.9396 2.45C4.07616 2.36833 4.21271 2.29833 4.34926 2.24L4.50289 1.085C4.54841 0.781667 4.68212 0.525 4.90402 0.315C5.12592 0.105 5.39049 0 5.69774 0H7.30226C7.60951 0 7.87408 0.105 8.09598 0.315C8.31788 0.525 8.45159 0.781667 8.49711 1.085L8.65073 2.24C8.79867 2.29833 8.93807 2.36833 9.06893 2.45C9.1998 2.53167 9.32782 2.61917 9.45299 2.7125L10.5113 2.2575C10.7958 2.12917 11.0803 2.1175 11.3648 2.2225C11.6492 2.3275 11.8711 2.51417 12.0305 2.7825L12.8327 4.2175C12.992 4.48583 13.0376 4.77167 12.9693 5.075C12.901 5.37833 12.7474 5.62917 12.5084 5.8275L11.6037 6.5275C11.6151 6.60917 11.6208 6.68792 11.6208 6.76375V7.23625C11.6208 7.31208 11.6094 7.39083 11.5867 7.4725L12.4913 8.1725C12.7303 8.37083 12.8839 8.62167 12.9522 8.925C13.0205 9.22833 12.975 9.51417 12.8157 9.7825L11.9963 11.2175C11.837 11.4858 11.6151 11.6725 11.3306 11.7775C11.0461 11.8825 10.7616 11.8708 10.4772 11.7425L9.45299 11.2875C9.32782 11.3808 9.19695 11.4683 9.0604 11.55C8.92384 11.6317 8.78729 11.7017 8.65073 11.76L8.49711 12.915C8.45159 13.2183 8.31788 13.475 8.09598 13.685C7.87408 13.895 7.60951 14 7.30226 14H5.69774ZM6.53414 9.45C7.19415 9.45 7.75744 9.21083 8.224 8.7325C8.69056 8.25417 8.92384 7.67667 8.92384 7C8.92384 6.32333 8.69056 5.74583 8.224 5.2675C7.75744 4.78917 7.19415 4.55 6.53414 4.55C5.86274 4.55 5.29661 4.78917 4.83574 5.2675C4.37487 5.74583 4.14443 6.32333 4.14443 7C4.14443 7.67667 4.37487 8.25417 4.83574 8.7325C5.29661 9.21083 5.86274 9.45 6.53414 9.45Z"
                      fill="black"
                      fill-opacity="0.5"
                    />
                  </svg>
                  Settings
                </span>
                <ChevronRightIcon className="text-gray-400" />
              </button>
              {inOnboarding && (
                <button
                  onClick={() => {
                    setIsUserMenuOpen(false);
                    setToursMenuOpen(true);
                  }}
                  className="w-full flex items-center justify-between px-3 py-1.5 text-[14px] text-[#00000080] hover:bg-[#F5F5F5] rounded-md transition-colors"
                >
                  <span className="flex items-center gap-2.5">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <circle cx="12" cy="12" r="9" />
                      <path d="M9.5 9a2.5 2.5 0 0 1 4.9.8c0 1.7-2.4 2.2-2.4 3.7" />
                      <line x1="12" y1="17" x2="12" y2="17.01" />
                    </svg>
                    Product tours
                  </span>
                  <ChevronRightIcon className="text-gray-400" />
                </button>
              )}
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2.5 px-3 py-1.5 text-[14px] text-[#00000080] hover:bg-[#F5F5F5] rounded-md transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="15"
                  viewBox="0 0 15 15"
                  fill="none"
                >
                  <path
                    d="M1.94052 14.35C1.50302 14.35 1.12849 14.1977 0.816939 13.893C0.505386 13.5884 0.349609 13.2222 0.349609 12.7944V1.90553C0.349609 1.47775 0.505386 1.11155 0.816939 0.80692C1.12849 0.50229 1.50302 0.349976 1.94052 0.349976H6.71325C6.93862 0.349976 7.12754 0.424513 7.28001 0.573587C7.43247 0.722661 7.5087 0.907383 7.5087 1.12775C7.5087 1.34812 7.43247 1.53285 7.28001 1.68192C7.12754 1.83099 6.93862 1.90553 6.71325 1.90553H1.94052V12.7944H6.71325C6.93862 12.7944 7.12754 12.869 7.28001 13.018C7.43247 13.1671 7.5087 13.3518 7.5087 13.5722C7.5087 13.7926 7.43247 13.9773 7.28001 14.1264C7.12754 14.2754 6.93862 14.35 6.71325 14.35H1.94052ZM11.6252 8.12775H5.91779C5.69241 8.12775 5.50349 8.05322 5.35103 7.90414C5.19857 7.75507 5.12234 7.57035 5.12234 7.34998C5.12234 7.12961 5.19857 6.94488 5.35103 6.79581C5.50349 6.64674 5.69241 6.5722 5.91779 6.5722H11.6252L10.1337 5.11386C9.98787 4.97127 9.91495 4.79627 9.91495 4.58886C9.91495 4.38146 9.98787 4.19998 10.1337 4.04442C10.2795 3.88886 10.4651 3.80785 10.6905 3.80136C10.9159 3.79488 11.1081 3.86942 11.2672 4.02498L14.111 6.80553C14.2701 6.96109 14.3496 7.14257 14.3496 7.34998C14.3496 7.55738 14.2701 7.73886 14.111 7.89442L11.2672 10.675C11.1081 10.8305 10.9192 10.9051 10.7005 10.8986C10.4817 10.8921 10.2928 10.8111 10.1337 10.6555C9.98787 10.5 9.91826 10.3153 9.92489 10.1014C9.93152 9.88748 10.0078 9.70924 10.1536 9.56664L11.6252 8.12775Z"
                    fill="black"
                    fill-opacity="0.5"
                  />
                  <path
                    d="M6.71289 0.175171C6.98112 0.175171 7.21518 0.265601 7.40234 0.448608C7.58957 0.631713 7.68349 0.861839 7.68359 1.12732C7.68359 1.39301 7.58971 1.62379 7.40234 1.80701C7.21518 1.99001 6.98112 2.08044 6.71289 2.08044H2.11523V12.6195H6.71289C6.98112 12.6195 7.21518 12.7099 7.40234 12.8929C7.58971 13.0762 7.68359 13.3069 7.68359 13.5726C7.68349 13.8381 7.58957 14.0682 7.40234 14.2513C7.21518 14.4343 6.98112 14.5248 6.71289 14.5248H1.94043C1.45744 14.5248 1.03871 14.3547 0.694336 14.0179C0.349973 13.6811 0.174805 13.2701 0.174805 12.7943V1.90564C0.174805 1.4299 0.349972 1.01883 0.694336 0.682007C1.03871 0.345281 1.45744 0.175192 1.94043 0.175171H6.71289ZM10.6855 3.62634C10.9576 3.61853 11.1961 3.71055 11.3896 3.89978L14.2334 6.68005C14.4234 6.86587 14.5244 7.0918 14.5244 7.34998C14.5244 7.60815 14.4234 7.83408 14.2334 8.0199L11.3896 10.8002C11.1972 10.9883 10.9624 11.0815 10.6953 11.0736C10.4319 11.0658 10.2016 10.9662 10.0117 10.7806L10.0059 10.7758L10.1338 10.6556L10.0059 10.7748C9.82844 10.5854 9.74198 10.3559 9.75 10.0961C9.75795 9.8395 9.85145 9.61764 10.0312 9.44177L11.1953 8.3031H5.91797C5.6498 8.3031 5.41566 8.2126 5.22852 8.02966C5.04114 7.84645 4.94727 7.61567 4.94727 7.34998C4.94727 7.08428 5.04114 6.8535 5.22852 6.67029C5.41566 6.48735 5.64979 6.39685 5.91797 6.39685H11.1953L10.0117 5.23865C9.83166 5.06259 9.74032 4.84268 9.74023 4.58923C9.74023 4.33615 9.83076 4.11209 10.0059 3.92517C10.1856 3.73349 10.4165 3.63408 10.6855 3.62634Z"
                    stroke="black"
                    stroke-opacity="0.5"
                    stroke-width="0.35"
                  />
                </svg>
                Logout
              </button>
              </div>,
              document.body
            )}
        </div>
      </nav>

      {/* Tooltip for collapsed-sidebar icons */}
      {!isOpen &&
        iconTip &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: iconTip.top,
              left: iconTip.left,
              transform: "translateY(-50%)",
            }}
            className="z-[100000] pointer-events-none font-outfit whitespace-nowrap bg-[#2C1338] text-white text-[13px] px-2.5 py-1.5 rounded-md shadow-[0_2px_8px_rgba(0,0,0,0.25)] border border-[rgba(255,255,255,0.12)]"
          >
            {iconTip.label}
          </div>,
          document.body
        )}

      {/* --------------------- Payment-expired modal ----------------------- */}
      {isShowPaymentPopup &&
        subscriptionStatus?.subscription_type?.toLowerCase() !==
          "paid_subs" && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg p-3 w-[360px] text-center">
              {(() => {
                const type = (
                  subscriptionStatus?.subscription_type || ""
                ).toLowerCase();
                const expiredType = (
                  subscriptionStatus?.expired_or_cancelled_subscription
                    ?.subscription_type || ""
                ).toLowerCase();

                if (
                  type === "subscription_expired" &&
                  expiredType === "subscription_expired"
                ) {
                  return (
                    <>
                      <h2 className="text-lg font-semibold mb-4">
                        🔒 Subscription Expired
                      </h2>
                      <p className="text-sm text-gray-600 mb-6">
                        Your subscription has expired. Please renew to continue
                        using premium features.
                      </p>
                    </>
                  );
                } else if (type === "trial_expired" || type === "none") {
                  return (
                    <>
                      <h2 className="text-lg font-semibold mb-4">
                        🚀 Your Trial Has Ended
                      </h2>
                      <p className="text-sm text-gray-600 mb-6">
                        Your free trial has ended. To continue enjoying premium
                        features, please upgrade your subscription. 💎
                      </p>
                    </>
                  );
                }
                return (
                  <>
                    <h2 className="text-lg font-semibold mb-4">
                      ⚠️ Subscription Inactive
                    </h2>
                    <p className="text-sm text-gray-600 mb-6">
                      We were unable to verify your subscription status.
                      <br />
                      To continue accessing premium features, please upgrade
                      your plan or contact our support team for assistance.
                    </p>
                  </>
                );
              })()}
              <div className="flex justify-between gap-3 mt-4">
                <button
                  onClick={handleLogout}
                  className="flex-1 border text-[#C95EBE] bg-white px-4 py-2 rounded-[6px] font-semibold text-sm hover:bg-[#C95EBE] hover:text-white active:bg-[#A844A7] transition-colors duration-300"
                >
                  🔓 Logout
                </button>
                <button
                  onClick={() => navigate("/payment")}
                  className="flex-1 border text-[#C95EBE] bg-white px-4 py-2 rounded-[6px] font-semibold text-sm hover:bg-[#C95EBE] hover:text-white active:bg-[#A844A7] transition-colors duration-300"
                >
                  🔁 Upgrade Now
                </button>
              </div>
            </div>
          </div>
        )}

      {/* ----------------------- Get-started celebration ------------------- */}
      {showGetStarted && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center">
          <img
            src="/leftside-celebration.svg"
            alt=""
            style={{
              position: "absolute",
              top: "50%",
              left: "32rem",
              transform: "translateY(-50%)",
              zIndex: 1,
            }}
          />
          <img
            src="/right-side-celebration.svg"
            alt=""
            style={{
              position: "absolute",
              top: "50%",
              left: "49rem",
              transform: "translateY(-50%)",
              zIndex: 1,
            }}
          />
          <div className="activation-popup">
            <img src="/celebrate-icon.svg" alt="" className="confetti-icon" />
            <h2>You're all set!</h2>
            <p>
              Ready to unlock the full potential of your documents?
              <br />
              Your 14-day free trial starts now!
            </p>
            <button
              className="activating-btn"
              onClick={() => {
                setShowGetStarted(false);
                window.location.href = "/ai-Copilot";
              }}
            >
              Get Started Now
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;

const gradientText = {
  background: "linear-gradient(90deg, #F3904F 30.06%, #8B55DD 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
};
